// Support-preferences aggregate dashboard tile — Vision & Strategy §5/§6.
//
// Counts-only view of how the org's employees have set their support
// preferences. Mirrors the privacy posture of the Engagement/Amber tiles:
// the headline `total` (employees who have set preferences) is always shown;
// the per-option breakdown is withheld when the pool is below the k-anon
// floor. This tile is a presentation layer — it must NOT compute thresholds
// or invent fields; `suppressed`/`threshold`/`total` come from the server.

import type { PreferencesAggregate } from '@besideu/shared/hr-dashboard';

export function PreferencesAggregateTile({ data }: { data: PreferencesAggregate }) {
  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Support preferences</h2>
        <p className="text-xs text-muted-foreground">
          Counts only · employee-set · k-anonymity floor applied
        </p>
      </header>

      <p
        className="text-3xl font-semibold tabular-nums"
        aria-label="Employees with set preferences"
      >
        {data.total}
      </p>
      <p className="mt-1 text-sm font-medium">
        {data.total === 1 ? 'employee has' : 'employees have'} set preferences
      </p>

      {data.suppressed ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {data.total === 0
            ? 'No employees have set support preferences yet.'
            : `Pool below the privacy floor of ${data.threshold}. The breakdown is withheld so an individual's choices can't be re-identified.`}
        </p>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Breakdown
            title="Contact method"
            rows={[
              ['Email', data.contact.email],
              ['Workspace', data.contact.workspace],
              ['No direct contact', data.contact.none],
            ]}
          />
          <Breakdown
            title="Touch points"
            rows={[
              ['Opted in', data.touchpoints.optIn],
              ['Opted out', data.touchpoints.optOut],
            ]}
          />
          <Breakdown
            title="Cadence"
            rows={[
              ['Standard', data.cadence.default],
              ['Lighter', data.cadence.lighter],
              ['None', data.cadence.none],
            ]}
          />
        </div>
      )}
    </section>
  );
}

function Breakdown({ title, rows }: { title: string; rows: Array<[string, number]> }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <dl className="mt-2 space-y-1">
        {rows.map(([label, n]) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-medium tabular-nums">{n}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
